import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface SectionConfig {
  key: string;
  enabled: boolean;
}

export interface HomepageLayout {
  sections: SectionConfig[];
  updatedAt?: Date;
}

// The reorderable homepage sections (the Hero banner stays fixed at the top).
// `editorPath`/`editorHint` link each section to where its content is managed.
export interface SectionDef {
  key: string;
  label: string;
  description: string;
  editorPath: string;
  editorHint: string;
}

export const SECTION_DEFS: SectionDef[] = [
  {
    key: 'benefits',
    label: 'Предимства',
    description: 'Лентата с иконки и предимства на комплекса',
    editorPath: '/admin/homepage',
    editorHint: 'Начална страница → таб „Предимства"',
  },
  {
    key: 'projectInfo',
    label: 'Удобства и услуги',
    description: 'Заглавие, описание и карти с характеристики',
    editorPath: '/admin/homepage',
    editorHint: 'Начална страница → таб „Удобства и услуги"',
  },
  {
    key: 'textImage',
    label: 'Текст + Снимка',
    description: 'Секция с текст отляво и снимка отдясно',
    editorPath: '/admin/content-blocks',
    editorHint: 'Текстови секции',
  },
  {
    key: 'imageText',
    label: 'Снимка + Текст',
    description: 'Секция със снимка отляво и текст отдясно',
    editorPath: '/admin/content-blocks',
    editorHint: 'Текстови секции',
  },
  {
    key: 'facade',
    label: 'Интерактивна фасада',
    description: 'Снимка на сградата с кликаеми етажи',
    editorPath: '/admin/facade',
    editorHint: 'Фасада',
  },
  {
    key: 'availability',
    label: 'Статистики',
    description: 'Числа и допълнителни удобства',
    editorPath: '/admin/availability',
    editorHint: 'Статистики',
  },
  {
    key: 'photoGallery',
    label: 'Галерия (снимки)',
    description: 'Слайдер със снимките от Медия (скрит, ако няма снимки)',
    editorPath: '/admin/media',
    editorHint: 'Медия',
  },
  {
    key: 'gallery',
    label: 'Локация / Карта',
    description: 'Google Maps секцията',
    editorPath: '/admin/location',
    editorHint: 'Локация',
  },
  {
    key: 'contact',
    label: 'Контактна форма',
    description: 'Формата за запитвания в дъното',
    editorPath: '',
    editorHint: 'Няма настройки',
  },
];

const defaultLayout: HomepageLayout = {
  sections: SECTION_DEFS.map((d) => ({ key: d.key, enabled: true })),
};

// Reconciles a stored layout with the known section definitions: keeps the
// saved order/visibility, drops unknown keys, and appends any new sections.
const reconcile = (stored?: SectionConfig[]): SectionConfig[] => {
  const known = new Set(SECTION_DEFS.map((d) => d.key));
  const seen = new Set<string>();
  const result: SectionConfig[] = [];

  (stored || []).forEach((s) => {
    if (known.has(s.key) && !seen.has(s.key)) {
      result.push({ key: s.key, enabled: s.enabled !== false });
      seen.add(s.key);
    }
  });
  SECTION_DEFS.forEach((d) => {
    if (!seen.has(d.key)) result.push({ key: d.key, enabled: true });
  });
  return result;
};

export const useHomepageLayout = () => {
  const [layout, setLayout] = useState<HomepageLayout>(defaultLayout);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLayout = async () => {
    try {
      setLoading(true);
      const snap = await getDoc(doc(db, 'content', 'homepageLayout'));
      if (snap.exists()) {
        const data = snap.data();
        setLayout({
          sections: reconcile(data.sections),
          updatedAt: data.updatedAt?.toDate(),
        });
      } else {
        setLayout(defaultLayout);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching homepage layout:', err);
      setError('Грешка при зареждане на подредбата');
      setLayout(defaultLayout);
    } finally {
      setLoading(false);
    }
  };

  const saveLayout = async (sections: SectionConfig[]) => {
    try {
      await setDoc(doc(db, 'content', 'homepageLayout'), {
        sections,
        updatedAt: serverTimestamp(),
      });
      setLayout({ sections, updatedAt: new Date() });
      return true;
    } catch (err) {
      console.error('Error saving homepage layout:', err);
      setError('Грешка при запазване на подредбата');
      return false;
    }
  };

  useEffect(() => {
    fetchLayout();
  }, []);

  return { layout, loading, error, saveLayout, refreshLayout: fetchLayout };
};
